// src/utils/reportHelpers.js

// ----- Display sanitization (safe for user-visible text) -----
// Strips only characters that could break HTML or cause injection.
// Preserves hyphens, apostrophes, etc. so names like "Chick-fil-A" remain correct.
export const sanitize = (s = '') =>
  String(s).replace(/[<>"'&]/g, '').trim().slice(0, 200);

// ----- Filename sanitization (aggressive, for download safety) -----
// Removes characters invalid in filenames across OSes.
// Regex matches: < > : " / \ | ? * and control characters (0x00-0x1F and 0x7F)
export const sanitizeFilename = (s = '') =>
  String(s).replace(/[<>:"/\\|?*\x00-\x1F\x7F]/g, '').trim().slice(0, 200);

/**
 * Formats a number as USD currency.
 * Non-number values (e.g., "Free", "Custom") are passed through as-is.
 * Use only with numeric pricing values from the backend.
 */
export const formatCurrency = (v) =>
  typeof v === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v)
    : v ?? 'Varies';

/**
 * Formats a decimal fraction as a percentage.
 * Expects a value between 0 and 1 (e.g., 0.25 → "25%").
 * Returns 'N/A' for null/undefined.
 */
export const formatPercent = (v) =>
  v !== undefined && v !== null ? `${Math.round(v * 100)}%` : 'N/A';

// ----- Generic slug humanizer with acronym awareness -----
// Converts a token like "lead_response" or "tenant_lifecycle" into
// "Lead Response" / "Tenant Lifecycle" while preserving common acronyms
// (HR, AI, CRM, EHR, MLS, POS, FCRA, etc.) instead of producing the
// half-formatted "Hr" / "Ai" / "Crm" Sarah Mitchell caught in R31-R32.
//
// Use anywhere category / cluster / problem-family slugs render to the
// buyer. Lifted from ProblemSolutionCard.jsx in R31.7 so PromptsTab,
// ToolsTab, and any future surface stay consistent.
const _ACRONYMS = new Set([
  'AI', 'API', 'CRM', 'MLS', 'POS', 'EHR', 'KPI', 'ROI',
  'SEO', 'CMS', 'CFO', 'COO', 'CMO', 'CPA', 'IT', 'QA',
  'AR', 'VR', 'UX', 'UI', 'IP', 'QR', 'SMB', 'ML',
  'NPS', 'PR', 'HR', 'GA', 'GTM', 'B2B', 'B2C', 'CSV',
  'PDF', 'SQL', 'URL', 'OS', 'IOT', 'FCRA', 'PCI', 'SOC',
  'GDPR', 'HIPAA', 'BAA', 'SOP', 'KYC', 'KYB', 'PII',
]);

// R46 (Maya R2 verdict): canonical display labels for category strings
// that humanizeSlug otherwise mangles by stripping hyphens. "E-commerce"
// → "E Commerce" was the visible regression on tool category chips.
// Lookup happens before the slugify pass so already-correct labels
// short-circuit. Keys are flat-lowercase to match any case variant.
const _CANONICAL_DISPLAY_LABELS = {
  'e-commerce':   'E-commerce',
  'ecommerce':    'E-commerce',
  'e commerce':   'E-commerce',
  'pci-dss':      'PCI-DSS',
  'med spa':      'Med Spa',
  'med-spa':      'Med Spa',
  'real estate':  'Real Estate',
  'real-estate':  'Real Estate',
};

export const humanizeSlug = (raw) => {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return s;
  const canonical = _CANONICAL_DISPLAY_LABELS[s.toLowerCase()];
  if (canonical) return canonical;
  return s
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      const upper = word.toUpperCase();
      if (_ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

// ----- Industry label helper -----
// Converts a slug like "real_estate" into a human-readable "Real Estate".
// Falls back to a sensible default when no industry is provided.
// Safe to call with unknown strings — anything unexpected just gets its
// underscores removed and each word capitalized.
export const formatIndustryLabel = (slug, fallback = 'your industry', subVertical = '') => {
  // R38 (Sarah R36c verdict): sub-vertical override — when the buyer's
  // report carries a sub_vertical hint (currently only "property_management";
  // future: dental specialties, healthcare sub-types, etc.) the displayed
  // industry label must reflect the sub-vertical, not the umbrella
  // industry. Without this, every report-page surface that calls this
  // helper renders "Real Estate" to a PM operator — Sarah's catastrophic
  // FAIL F on R36c.
  const _SUB_VERTICAL_LABELS = {
    property_management: 'Property Management',
  };
  if (subVertical && _SUB_VERTICAL_LABELS[String(subVertical).toLowerCase()]) {
    return _SUB_VERTICAL_LABELS[String(subVertical).toLowerCase()];
  }
  if (!slug || typeof slug !== 'string') return fallback;
  // R29.1 (Sarah R29 verdict): canonical-casing for industry slugs that
  // don't match standard title-case. Sarah caught "Hvac" / "Quickbooks" /
  // "Saas" leaking into UI as title-cased garbage. Industries with
  // brand-specific casing get an explicit override; everything else
  // falls through to the title-case path.
  const CANONICAL_INDUSTRY_LABELS = {
    hvac:                 'HVAC',
    saas:                 'SaaS',
    ecommerce:            'E-commerce',
    'e-commerce':         'E-commerce',
    real_estate:          'Real Estate',
    'real-estate':        'Real Estate',
    home_services:        'Home Services',
    auto_repair:          'Auto Repair',
    med_spa:              'Med Spa',
    medspa:               'Med Spa',
    professional_services:'Professional Services',
    credit_repair:        'Credit Repair',
    mental_health:        'Mental Health',
    physical_therapy:     'Physical Therapy',
    property_management:  'Property Management',
  };
  const key = slug.toLowerCase().trim();
  if (CANONICAL_INDUSTRY_LABELS[key]) return CANONICAL_INDUSTRY_LABELS[key];
  return slug
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

// R44 (Maya verdict): possessive form for business names. The naive
// template `${name}'s` produces "Wildroot Botanicals's" for names
// ending in 's' — looks amateur across a huge slice of real
// businesses (Adams, Wells, Davis, Jones, James, Sons, Services,
// Solutions, Group, etc.). Standard English style: names ending in
// 's' or 'z' take a bare apostrophe ("Wildroot Botanicals'") in
// modern writing-style guides (AP, Chicago for proper nouns).
//
// Usage: possessive('Wildroot Botanicals') → "Wildroot Botanicals'"
//        possessive('Cedar Ridge') → "Cedar Ridge's"
//        possessive('') → 'your' (caller-supplied fallback uses
//        ternary at call site if needed)
export const possessive = (name) => {
  if (!name || typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  const lastChar = trimmed.charAt(trimmed.length - 1).toLowerCase();
  // Names ending in 's' or 'z' take bare apostrophe; everything else
  // takes apostrophe-s. Special-case "us"/"is" word-endings (Jesus',
  // Moses', etc.) inherit the same rule. Edge case: ALL_CAPS acronyms
  // ending in 'S' (e.g. "JBS") follow the same bare-apostrophe rule.
  if (lastChar === 's' || lastChar === 'z') return `${trimmed}'`;
  return `${trimmed}'s`;
};

// ----- Business type label helper -----
// Maps form slugs to user-facing labels for the audit-form review step
// (was leaking raw values like "services", "ecommerce" before).
export const formatBusinessType = (slug, fallback = 'Not set') => {
  if (!slug || typeof slug !== 'string') return fallback;
  const TYPES = {
    services:  'Services business',
    physical:  'Physical / brick-and-mortar',
    ecommerce: 'E-commerce',
    both:      'Both physical & online',
  };
  return TYPES[slug.toLowerCase()] || formatIndustryLabel(slug, fallback);
};

// ----- Budget range label helper -----
// Maps form slugs (under-2k, 2k-5k, ...) to dollar-range labels. Returns
// just the range (e.g. "$500-$2K") so the caller appends its own "/mo"
// suffix once and we don't end up with the "Under $2K / month/month"
// stuttering QA caught at Step 4 review. Includes every slug currently
// emitted by the audit form's budget picker.
export const formatBudgetRange = (slug, fallback = 'Not set') => {
  if (!slug || typeof slug !== 'string') return fallback;
  const RANGES = {
    'under-200':  'Under $200',
    'under-500':  '$200-$500',
    'under-2k':   '$500-$2K',
    'under-5k':   'Under $5K',
    '2k-5k':      '$2K-$5K',
    '5k-10k':     '$5K-$10K',
    '10k-25k':    '$10K-$25K',
    '25k-plus':   '$25K+',
    'over-10k':   '$10K+',
    '150k-plus':  '$150K+',
  };
  return RANGES[slug.toLowerCase()] || slug;
};

// ----- Industry benchmark helper -----
export const getIndustryBenchmark = (industryBenchmarks, industry, metricKey) => {
  const ind = (industry || '').toLowerCase();
  const benchmark = industryBenchmarks?.[ind] ?? industryBenchmarks?.['default'];
  if (!benchmark) return null;
  const metric = benchmark[metricKey];
  if (!metric) return null;
  return {
    stat:        metric.stat ?? metric.value,
    description: metric.description,
    sampleSize:  metric.sample_size ?? metric.sampleSize,
    timeframe:   metric.timeframe ?? '60 days',
  };
};

// ----- Alternative edge helper -----
export const getAlternativeEdge = (alt, primaryTool, industry) => {
  const altPrice  = typeof alt.pricing === 'number' ? alt.pricing : null;
  const primPrice = typeof primaryTool.pricing === 'number' ? primaryTool.pricing : null;

  if (altPrice !== null && primPrice !== null && altPrice < primPrice * 0.8)
    return { label: `${Math.round((1 - altPrice / primPrice) * 100)}% cheaper`, color: 'emerald' };

  if (alt.best_for_industries?.includes((industry || '').toLowerCase()))
    return { label: `Best for ${industry}`, color: 'blue' };

  if (alt.integration_complexity === 'native' && primaryTool.integration_complexity !== 'native')
    return { label: 'Easier setup', color: 'purple' };

  // Match score branch removed for consistency (no longer shown in UI)
  return null;
};

// ----- Single source of truth for color classes -----
export const colorClasses = {
  emerald: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  blue:    { bg: 'bg-blue-500/20',    text: 'text-blue-400'    },
  purple:  { bg: 'bg-purple-500/20',  text: 'text-purple-400'  },
  amber:   { bg: 'bg-amber-500/20',   text: 'text-amber-400'   },
};

// Aliases for backward compatibility during migration
export const edgeColorClasses = colorClasses;
export const iconColorClasses = colorClasses;

// ----- Build a context-rich Ask AI setup prompt -----
export const buildSetupPrompt = (tool, { businessName, industry, assistantLabel } = {}) => {
  const ai = assistantLabel && assistantLabel !== 'your AI assistant' ? assistantLabel : null;
  const steps = Array.isArray(tool.setupSteps) && tool.setupSteps.length
    ? `\n\nMy setup plan so far:\n${tool.setupSteps.slice(0, 4).map((s, i) => `${i + 1}. ${s}`).join('\n')}`
    : '';
  const gotchas = tool.failure_common_reasons
    ? `\n\nKnown failure points: ${Array.isArray(tool.failure_common_reasons) ? tool.failure_common_reasons.join('; ') : tool.failure_common_reasons}`
    : '';
  const proof = tool.successStory
    ? `\n\nWhat similar businesses achieved: ${tool.successStory}`
    : '';
  return `I'm a ${industry || 'small business'} owner (${businessName || 'my business'}) implementing ${tool.name}${ai ? ` with ${ai}` : ''}.

Why I chose it: ${tool.why || 'streamline operations and reduce manual work'}${steps}${gotchas}${proof}

Based on everything above, answer in under 200 words:
1. The 1-2 configurations that will have the biggest impact for a ${industry || 'small'} business specifically — not generic setup advice
2. How I'll know it's working after 48 hours (one concrete test)
3. The single most important pitfall to avoid given my failure points above`;
};

// ----- Shared severity styles -----
export const severityStyles = {
  HIGH:   { border: 'border-l-red-500',   bg: 'bg-red-900/10',   icon: 'text-red-400',   badge: 'bg-red-500/20 text-red-400' },
  MEDIUM: { border: 'border-l-amber-500', bg: 'bg-amber-900/10', icon: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-400' },
  LOW:    { border: 'border-l-blue-500',  bg: 'bg-blue-900/10',  icon: 'text-blue-400',  badge: 'bg-blue-500/20 text-blue-400' },
};

// ----- ROI calculation helper -----
// Given phases array and impact index, returns Year 1 investment, returns, and net.
export const computeY1ROI = (phases, impactIdx = 0.25, roiData = null) => {
  const totalToolCost = phases.reduce((sum, p) => {
    const cost = p.estimated_monthly_cost ?? 0;
    const weeks = p.weeks ?? 4; // default to 1 month if missing
    return sum + cost * (weeks / 4);
  }, 0);

  // Prefer backend-computed components over synthetic formula
  const monthlySavings = roiData?.estimated_monthly_savings || 0;
  const monthlyRevenue = roiData?.estimated_monthly_revenue_uplift || 0;
  const monthlyTotal   = monthlySavings + monthlyRevenue;
  const annualTotal    = monthlyTotal * 12;
  const toolCost       = roiData?.y1_investment_estimate || 0;

  // Only fall back to impactIdx formula if backend returned nothing
  if (annualTotal > 0) {
    return {
      y1Returns: annualTotal,
      y1Net:     annualTotal - toolCost,
      source:    "backend",
    };
  }

  // Legacy fallback (backend data missing)
  const baseFloor  = totalToolCost > 500 ? totalToolCost : 15000;
  const y1Returns  = baseFloor * (1 + impactIdx * 6);
  return {
    y1Returns,
    y1Net:   y1Returns - baseFloor,
    source:  "estimate",
  };
};

// ----- Page estimate helper -----
// Estimates the number of pages in the report based on content.
export const computePageEstimate = (fullReport, phases, tools, matchedProblems) => {
  const emergingTools = fullReport?.emergingTools || [];
  const playbooks = fullReport?.playbooks || [];

  let pages = 0;

  // Calibrated against actual ReportLab output (~19-25pp for a typical business)
  pages += phases.length * 0.7;                           // roadmap phases + timeline
  pages += Math.min(tools.length, 10) * 0.6;             // tool cards (capped: multiple tools share a page)
  pages += Math.min(emergingTools.length, 8) * 0.25;     // hidden-gem table rows
  pages += Math.min(matchedProblems.length, 8) * 0.35;   // diagnosis rows
  pages += playbooks.length * 0.5;                        // playbook blocks

  // Fixed sections: cover, TOC, exec summary, team adoption, ROI, competitive, risk/checklist, prompts
  pages += 10;

  return Math.min(Math.max(Math.round(pages), 18), 30);
};
